import { useJSONFetcher } from "@outplayed/json-fetcher";
import SubtitleAndDescription from "components/Common/SubtitleAndDescription";
import React from "react";
import { useResponsive } from "@outplayed/responsive";
import { API_WOW_ROOT_PATH } from "../../../api/apiHelpers";
import { useQueryString } from "../../../hooks/general-hooks";
import { getEncounterId, useWowPath } from "../../../hooks/path-hooks";
import {
  EXPANSION,
  getAbbreviatedClass,
  getAbbreviatedSpec,
  getDifficultyPath,
  getFormattedContentType,
  getFormattedEncounterType,
  getFormattedNames,
  getIcyVeinsLinkName,
  getIcyVeinsRole,
  PATCH,
} from "../../../lib/wowHelpers";
import NoData from "../../Common/NoData";
import PvEGearGuide from "../../GearGuide/PvEGearGuide";
import TierSetContainer from "../Builds/TierSetContainer";
import Layout from "../Layout";
import EmbellishmentPairs from "./EmbellishmentPairs";
import GearContainer, { getPrimaryMetric, ItemsSubContainerCategory } from "./GearContainer";
import PageSectionCrossLinks from "../../CrossLinks/PageSectionCrossLinks";
import { SPECS_TO_HERO_TREES } from "lib/classes-and-specs";
import { TablePageSkeleton } from "components/Common/PageSkeletons";

const GearPageContainer = () => {
  const { currentBreakpoint } = useResponsive();
  const isMobile =
    currentBreakpoint === "MOBILE_SMALL" || currentBreakpoint === "MOBILE_MEDIUM" || currentBreakpoint === "MOBILE_LARGE";

  const { classType, specType, encounterType, optionalParams } = useWowPath();
  const encounterID = getEncounterId(optionalParams, encounterType);
  const { displayClassName, displaySpecName, dataClassName, dataSpecName } = getFormattedNames(classType, specType);

  const { difficulty, affix } = useQueryString();
  const diffPath = getDifficultyPath(difficulty, encounterType);
  const { data: gearTable, loading: isLoading } = useJSONFetcher(
    `${API_WOW_ROOT_PATH}/${diffPath}/${dataClassName}/${dataClassName}_itemsTable.json`,
    { ssr: true },
  );

  const pageDescription = `Data-driven ${displaySpecName} ${displayClassName} recommended gear guide from top players. Follow these ${
    SPECS_TO_HERO_TREES[displayClassName].specs[displaySpecName][0]
  } and ${SPECS_TO_HERO_TREES[displayClassName].specs[displaySpecName][1]} ${getAbbreviatedSpec(
    displaySpecName,
  )} ${getAbbreviatedClass(displayClassName)} gear recommendations on your journey to BIS for ${getFormattedEncounterType(
    encounterType,
  )} in ${EXPANSION} ${PATCH}.`;

  const metaDescription = `Data-driven ${displaySpecName} ${displayClassName} gear from top players. The best ${getAbbreviatedSpec(
    displaySpecName,
  )} ${getAbbreviatedClass(displayClassName)} gear recommendations in each slot for ${getFormattedEncounterType(
    encounterType,
  )} in ${EXPANSION} ${PATCH}.`;

  const layoutProps = {
    className: "wow-gear-page-container",
    classType,
    filterPage: "builds",
    metaTitle: `${displaySpecName} ${displayClassName} Gear for ${getFormattedEncounterType(encounterType)}`,
    metaDescription,
    pageDescription,
    pageTitle: `Gear for ${getFormattedEncounterType(encounterType)}`,
    specType,
  };

  if (isLoading) {
    return (
      <Layout {...layoutProps}>
        <TablePageSkeleton />
      </Layout>
    );
  }

  const dataClassSpec = `${dataClassName}-${dataSpecName}`;
  const hasData =
    gearTable &&
    (affix && affix !== "all"
      ? (gearTable.affixes?.[affix]?.[encounterID]?.[dataClassSpec]?.items_table?.items?.belt?.total_count ?? 0) > 0
      : (gearTable[encounterType]?.[encounterID]?.[dataClassSpec]?.items_table?.items?.belt?.total_count ?? 0) > 0);

  const classSpecGear =
    hasData &&
    (affix && affix !== "all"
      ? gearTable.affixes[affix][encounterID][dataClassSpec].items_table
      : gearTable[encounterType][encounterID][dataClassSpec].items_table);
  const bisGear =
    hasData &&
    (affix && affix !== "all"
      ? gearTable.affixes[affix][encounterID][dataClassSpec]
      : gearTable[encounterType][encounterID][dataClassSpec]);

  const specClass = `${displaySpecName} ${displayClassName}`;
  const specClass2 = `${getAbbreviatedSpec(displaySpecName)} ${getAbbreviatedClass(displayClassName)}`;

  // The backend publishes crafted gear with bonus IDs upgraded to the max
  // crafting rank (the rank an optimizing player would buy), so tooltips carry
  // quality tier 5. Each list is capped to the most popular entries; the long
  // tail is sub-1% noise.
  const craftedGear = ((classSpecGear && classSpecGear.crafted_gear) || [])
    .map((item) => ({ ...item, crafted_quality: 5 }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);
  const embellishmentPairs = ((classSpecGear && classSpecGear.embellishment_pairs) || []).slice(0, 5);
  const hasCraftedData = craftedGear.length > 0 || embellishmentPairs.length > 0;
  const contentType = getFormattedContentType(encounterType);
  const ivGearGuideURL = `https://www.icy-veins.com/wow/${getIcyVeinsLinkName(specType)}-${getIcyVeinsLinkName(
    classType,
  )}-pve-${getIcyVeinsRole(specType)}-gear-best-in-slot`;

  return (
    <Layout {...layoutProps}>
      {hasData ? (
        <>
          <PvEGearGuide
            builds={bisGear.items}
            combos={bisGear.combos}
            classType={classType}
            specType={specType}
            encounterType={encounterType}
            encounter={encounterID}
          />
          {/* <TierSetContainer className={displayClassName} specName={displaySpecName} heroSpecName={dataHeroName} encounterType={encounterType} /> */}
          {hasCraftedData && (
            <>
              <div className="seperator" />
              <SubtitleAndDescription
                subtitle={`${specClass2} Best Crafted Items`}
                description={`The most popular crafted items and embellishment combinations for ${contentType}.`}
              />
              <div className="wow-items-page crafted-items-container">
                {craftedGear.length > 0 && (
                  <ItemsSubContainerCategory
                    category="Crafted Items"
                    metricOneName={getPrimaryMetric(displaySpecName, encounterType)}
                    metricTwoName="Popularity"
                    data={craftedGear}
                    count={craftedGear.reduce((acc, x) => acc + x.count, 0)}
                  />
                )}
                {embellishmentPairs.length > 0 && (
                  <EmbellishmentPairs data={embellishmentPairs} builds={bisGear.items} slotItems={classSpecGear.items} />
                )}
              </div>
            </>
          )}
          <div className="seperator" />
          <SubtitleAndDescription
            subtitle={`${specClass2} Gear Tables`}
            description={`These tables are a comparison of the most popular items in each gear slot for ${contentType}.`}
          />
          <GearContainer
            classSpecGear={classSpecGear}
            isMobile={isMobile}
            displaySpecName={displaySpecName}
            encounterType={encounterType}
          />
          <PageSectionCrossLinks
            classType={classType}
            description={`Our friends at Icy Veins have you covered, with an in-depth guide to suit up your ${displaySpecName} ${displayClassName} with the best gear.`}
            guideData={[
              {
                description: `Find the best in slot gear for ${displaySpecName} ${displayClassName}`,
                link: ivGearGuideURL,
                title: "Gear and Best in Slot Guide",
              },
            ]}
            specType={specType}
          />
        </>
      ) : (
        <NoData specType={specType} />
      )}
    </Layout>
  );
};

export default GearPageContainer;
